import { useState, useEffect, useRef, useCallback } from 'react';
import { HourlyForecastTimelineData } from '../models/HourlyForecastTimelineData';
import { NotificationService } from '../services/NotificationService';

interface NotificationState {
  showNotification: boolean;
  notificationSeverity: 'error' | 'warning' | 'info';
  notificationTitle: string;
  notificationMessage: string;
}

/**
 * Custom hook to handle periodic data checking and notification management
 */
export function useNotificationCheck(params: {
  timelineData: HourlyForecastTimelineData[];
  loading: boolean;
  hasCustomNow: boolean;
  error: string | null;
  checkInterval?: number;
}): NotificationState {
  const { timelineData, loading, hasCustomNow, error, checkInterval = 180000 } = params;
  
  // Notification state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationSeverity, setNotificationSeverity] = useState<'error' | 'warning' | 'info'>('warning');
  const [notificationTitle, setNotificationTitle] = useState('Warning');
  const [notificationMessage, setNotificationMessage] = useState('');
  
  // Track current notification for comparison
  const currentNotificationId = useRef<string | null>(null);
  
  // Function to check for data issues
  const checkForDataIssues = useCallback(() => {
    const issue = NotificationService.checkForDataIssues({
      timelineData,
      loading,
      hasCustomNow,
      error
    });
    
    if (issue) {
      // Only update if this is a new notification or if it's different from the current one
      if (issue.id !== currentNotificationId.current) {
        setNotificationSeverity(issue.type);
        setNotificationTitle(issue.title);
        setNotificationMessage(issue.message);
        setShowNotification(true);
        currentNotificationId.current = issue.id;
      }
    } else {
      // No issues found, hide notification
      setShowNotification(false);
      currentNotificationId.current = null;
    }
  }, [timelineData, loading, hasCustomNow, error]);
  
  // Set up periodic checks for data issues
  useEffect(() => {
    // Skip checks if using custom now parameter
    if (hasCustomNow) return;
    
    // Initial check after data is loaded
    if (!loading && timelineData.length > 0) {
      checkForDataIssues();
    }
    
    // Setup interval for regular checks (using the provided interval)
    const intervalId = setInterval(() => {
      checkForDataIssues();
    }, checkInterval);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [timelineData, loading, checkForDataIssues, hasCustomNow, checkInterval]);
  
  // Run check when data changes or refreshes
  useEffect(() => {
    if (!loading && timelineData.length > 0) {
      checkForDataIssues();
    }
  }, [timelineData, loading, checkForDataIssues]);
  
  return {
    showNotification,
    notificationSeverity,
    notificationTitle,
    notificationMessage
  };
} 